import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, Search, X } from 'lucide-react';
import { api } from '@/lib/api';
import { useDebounce } from '@/hooks/useDebounce';
import { cn } from '@/lib/utils';

/**
 * Campo de produto com autocomplete — código ou descrição.
 *
 * Digitar um código e não ver o nome é digitar no escuro: 12745 e 12754 são
 * indistinguíveis até a mercadoria chegar errada no outro centro. Por isso o
 * campo só considera o produto ESCOLHIDO quando ele foi selecionado da lista,
 * e a partir daí mostra o nome ao lado.
 *
 * Busca no CATÁLOGO, não no estoque: um produto sem saldo na origem aparece
 * aqui de propósito. A recusa por saldo vem na aprovação, com mensagem própria
 * dizendo quanto falta — some do autocomplete seria esconder o motivo.
 */

export interface ProdutoEscolhido {
  produto: number;
  descricao: string | null;
}

interface Sugestao {
  produto: number;
  descricao: string | null;
  ean: string | null;
}

export function CampoProduto({
  valor,
  aoEscolher,
  aoLimpar,
  rotuloAcessivel,
}: {
  valor: ProdutoEscolhido | null;
  aoEscolher: (p: ProdutoEscolhido) => void;
  aoLimpar: () => void;
  rotuloAcessivel: string;
}): JSX.Element {
  const [termo, definirTermo] = React.useState('');
  const [aberto, definirAberto] = React.useState(false);
  const [destaque, definirDestaque] = React.useState(0);
  const caixa = React.useRef<HTMLDivElement>(null);
  const atrasado = useDebounce(termo, 250);

  const consulta = useQuery<{ produtos: Sugestao[] }>({
    queryKey: ['produtos-buscar', atrasado],
    queryFn: () => api.get<{ produtos: Sugestao[] }>('/api/produtos/buscar', { q: atrasado }),
    enabled: aberto && atrasado.trim().length >= 2,
    staleTime: 5 * 60_000,
  });

  const sugestoes = consulta.data?.produtos ?? [];

  // Clicar fora fecha: lista de sugestões que fica aberta cobre o resto do
  // formulário e some com o botão de salvar.
  React.useEffect(() => {
    if (!aberto) return;
    const fora = (e: MouseEvent): void => {
      if (caixa.current && !caixa.current.contains(e.target as Node)) definirAberto(false);
    };
    document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, [aberto]);

  const escolher = (s: Sugestao): void => {
    aoEscolher({ produto: s.produto, descricao: s.descricao });
    definirTermo('');
    definirAberto(false);
  };

  if (valor !== null) {
    return (
      <span className="flex min-w-0 flex-1 items-center gap-2 rounded-lg border border-borda bg-superficie2 px-2 py-1">
        <Check className="h-3.5 w-3.5 shrink-0 text-sucesso" />
        <span className="tabular shrink-0 font-mono text-[13px] font-bold">{valor.produto}</span>
        <span className="min-w-0 flex-1 truncate text-[12px]" title={valor.descricao ?? ''}>
          {valor.descricao ?? 'sem descrição'}
        </span>
        <button
          type="button"
          onClick={aoLimpar}
          aria-label="Trocar produto"
          className="shrink-0 text-textoFraco hover:text-texto"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </span>
    );
  }

  return (
    <div ref={caixa} className="relative min-w-0 flex-1">
      <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2">
        <Search className="h-3.5 w-3.5 text-textoFraco" />
      </span>
      <input
        value={termo}
        onChange={(e) => {
          definirTermo(e.target.value);
          definirAberto(true);
          definirDestaque(0);
        }}
        onFocus={() => definirAberto(true)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            definirDestaque((d) => Math.min(d + 1, sugestoes.length - 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            definirDestaque((d) => Math.max(d - 1, 0));
          } else if (e.key === 'Enter') {
            e.preventDefault();
            const s = sugestoes[destaque];
            if (s) escolher(s);
          } else if (e.key === 'Escape') {
            definirAberto(false);
          }
        }}
        aria-label={rotuloAcessivel}
        autoComplete="off"
        placeholder="código ou nome do produto"
        className="min-h-[36px] w-full rounded-lg border border-borda bg-superficie pl-7 pr-2 text-[13px] outline-none focus:border-primaria focus:ring-2 focus:ring-primaria/25"
      />

      {aberto && termo.trim().length >= 2 ? (
        <div className="absolute left-0 right-0 top-[38px] z-20 max-h-[240px] overflow-y-auto rounded-lg border border-borda bg-superficie shadow-forte">
          {consulta.isLoading ? (
            <p className="px-3 py-2 text-[12px] text-textoFraco">Procurando…</p>
          ) : sugestoes.length === 0 ? (
            <p className="px-3 py-2 text-[12px] text-textoFraco">Nenhum produto encontrado.</p>
          ) : (
            <ul>
              {sugestoes.map((s, i) => (
                <li key={s.produto}>
                  <button
                    type="button"
                    onMouseEnter={() => definirDestaque(i)}
                    onClick={() => escolher(s)}
                    className={cn(
                      'flex w-full items-baseline gap-2 px-3 py-1.5 text-left',
                      i === destaque ? 'bg-primaria-suave' : 'hover:bg-superficie2',
                    )}
                  >
                    <span className="tabular shrink-0 font-mono text-[12px] font-bold">
                      {s.produto}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-[12px]">
                      {s.descricao ?? '—'}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}
